import {
  getCloudBaseWebClient,
  type CloudBaseWebClient,
  type CloudBaseWebConfig,
} from "../../../lib/cloudBaseWebClient";
import { M1ApiError } from "../../m1-app/data/M1AppApi";

export type CloudBaseAdminSessionConfig = CloudBaseWebConfig;

export type CloudBaseAdminSession = {
  getAccessToken(): Promise<string>;
  getCurrentUsername(): Promise<string | null>;
  signIn(username: string, password: string): Promise<string>;
  signOut(): Promise<void>;
};

function userLabel(user: {
  id?: unknown;
  email?: unknown;
  phone?: unknown;
  user_metadata?: Record<string, unknown>;
}, fallback = "管理员"): string {
  const value = user.user_metadata?.username
    ?? user.user_metadata?.name
    ?? user.email
    ?? user.phone
    ?? user.id
    ?? fallback;
  return typeof value === "string" ? value : String(value);
}

function loginErrorMessage(reason: unknown): string {
  const message = reason instanceof Error
    ? reason.message
    : typeof reason === "object" && reason && "message" in reason
      ? String(reason.message)
      : "";
  const normalized = message.toLowerCase();

  if (/network|fetch|timeout|timed out|连接|网络/.test(normalized)) {
    return "网络连接异常，请检查网络后重试";
  }
  if (/password|credential|user.*not.*found|invalid.*login|账号|密码/.test(normalized)) {
    return "账号或密码不正确，请重新输入";
  }
  if (/provider|strategy|not enabled|未启用/.test(normalized)) {
    return "账号密码登录尚未启用，请联系系统管理员";
  }
  return "登录失败，请稍后重试";
}

function errorMessage(reason: unknown): string {
  if (reason instanceof Error) return reason.message;
  if (typeof reason === "object" && reason && "message" in reason) return String(reason.message);
  return String(reason ?? "");
}

function isTransientSessionError(reason: unknown): boolean {
  const message = errorMessage(reason).toLowerCase();
  return /network|fetch|timeout|timed out|连接|网络|cors|abort|offline|503|502|504/.test(message);
}

export function createCloudBaseAdminSession(
  config: CloudBaseAdminSessionConfig,
  client: CloudBaseWebClient = getCloudBaseWebClient(config),
): CloudBaseAdminSession {
  const auth = client.auth;

  async function readSession() {
    for (let attempt = 0; attempt < 2; attempt += 1) {
      try {
        const { data, error } = await auth.getSession();
        if (error) {
          if (!isTransientSessionError(error)) {
            // A rejected refresh token is an ordinary signed-out state. The
            // next login replaces the SDK credentials with a fresh session.
            return null;
          }
          if (attempt === 0) continue;
          throw new M1ApiError("SESSION_CHECK_FAILED", "暂时无法验证登录状态，请检查网络后重试");
        }
        const session = data?.session;
        if (!session || session.user?.is_anonymous || !session.access_token) return null;
        return session;
      } catch (reason) {
        if (!isTransientSessionError(reason)) return null;
        if (attempt === 0) continue;
        if (reason instanceof M1ApiError) throw reason;
        throw new M1ApiError("SESSION_CHECK_FAILED", "暂时无法验证登录状态，请检查网络后重试");
      }
    }
    return null;
  }

  return {
    async getAccessToken() {
      const session = await readSession();
      if (!session) throw new M1ApiError("UNAUTHORIZED", "登录状态已失效，请重新登录");
      return session.access_token;
    },
    async getCurrentUsername() {
      const session = await readSession();
      return session ? userLabel(session.user) : null;
    },
    async signIn(username, password) {
      try {
        const { data, error } = await auth.signInWithPassword({ username: username.trim(), password });
        if (error || !data?.session || data.session.user?.is_anonymous) {
          throw new M1ApiError("LOGIN_FAILED", loginErrorMessage(error));
        }
        return userLabel(data.session.user, username.trim());
      } catch (reason) {
        if (reason instanceof M1ApiError) throw reason;
        throw new M1ApiError("LOGIN_FAILED", loginErrorMessage(reason));
      }
    },
    async signOut() {
      await auth.signOut();
    },
  };
}
